<template>
  <div class="seleccion-page">
    <div class="seleccion-bg"></div>

    <div class="seleccion-content">
      <div class="asignar-boleto-card seleccion-card">
        <!-- Cargando -->
        <div v-if="cargando" class="estado-center">
          <div class="spinner-border text-primary mb-3" role="status"></div>
          <p class="text-muted">Cargando peleadores...</p>
        </div>

        <!-- Link inválido -->
        <div v-else-if="linkInvalido" class="estado-center">
          <div class="estado-icon">❌</div>
          <h4>Link inválido</h4>
          <p class="text-muted">
            Este enlace no existe o ya expiró. Contáctanos si necesitas ayuda.
          </p>
        </div>

        <!-- Ya registrado antes -->
        <div v-else-if="completo" class="estado-center">
          <div class="estado-icon">✅</div>
          <h4>Ya tienes tu peleador</h4>
          <p class="text-muted">
            Ya registraste tu selección anteriormente. ¡Nos vemos en el evento!
          </p>
        </div>

        <!-- Formulario -->
        <form v-else-if="!registrado" @submit.prevent="enviar">
          <div class="form-header">
            <span class="form-kicker">Selecciona a tu peleador</span>
            <h3 class="form-titulo">¿A quién vas a alentar?</h3>
            <p class="form-subtitulo">{{ nombreEvento }}</p>

            <div v-if="comprasPendientes.length > 1" class="form-progreso">
              <div class="progreso-texto">
                {{ seleccionadosCount }} de {{ comprasPendientes.length }} boletos listos
              </div>
              <div class="progreso-bar">
                <div class="progreso-fill" :style="{ width: progresoPct + '%' }"></div>
              </div>
            </div>
          </div>

          <div
            v-for="(compra, idx) in comprasPendientes"
            :key="compra.id"
            class="compra-seccion"
            :class="{ 'is-completa': selecciones[compra.id] }"
          >
            <div class="compra-header">
              <span class="compra-badge">{{ idx + 1 }}</span>
              <span class="compra-titulo-text">{{ compra.tipo_boleto }}</span>
              <i v-if="selecciones[compra.id]" class="bi bi-check-circle-fill compra-check"></i>
            </div>

            <div class="combates-lista">
              <div v-for="c in combates" :key="c.id" class="combate-row">
                <button
                  type="button"
                  class="peleador-opcion"
                  :class="{
                    'is-selected': selecciones[compra.id] === c.peleador_a.id,
                    'is-disabled': !c.peleador_a.id,
                  }"
                  :disabled="!c.peleador_a.id"
                  @click="elegir(compra.id, c.peleador_a.id)"
                >
                  <img
                    :src="c.peleador_a.foto || avatarDefault"
                    :alt="c.peleador_a.nombre"
                    class="peleador-foto"
                    loading="lazy"
                    decoding="async"
                  />
                  <span class="peleador-nombre">{{ c.peleador_a.nombre }}</span>
                  <i
                    v-if="selecciones[compra.id] === c.peleador_a.id"
                    class="bi bi-check-circle-fill check-icon"
                  ></i>
                </button>

                <span class="vs-label">VS</span>

                <button
                  type="button"
                  class="peleador-opcion"
                  :class="{
                    'is-selected': selecciones[compra.id] === c.peleador_b.id,
                    'is-disabled': !c.peleador_b.id,
                  }"
                  :disabled="!c.peleador_b.id"
                  @click="elegir(compra.id, c.peleador_b.id)"
                >
                  <img
                    :src="c.peleador_b.foto || avatarDefault"
                    :alt="c.peleador_b.nombre"
                    class="peleador-foto"
                    loading="lazy"
                    decoding="async"
                  />
                  <span class="peleador-nombre">{{ c.peleador_b.nombre }}</span>
                  <i
                    v-if="selecciones[compra.id] === c.peleador_b.id"
                    class="bi bi-check-circle-fill check-icon"
                  ></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Error -->
          <div
            v-if="errorMsg"
            class="alert alert-danger py-2 mb-3"
            style="font-size: 0.9rem"
          >
            {{ errorMsg }}
          </div>

          <div class="d-grid">
            <button class="btn btn-primary btn-lg" type="submit" :disabled="enviando">
              <span v-if="enviando" class="spinner-border spinner-border-sm me-2"></span>
              {{ enviando ? "Guardando..." : "Confirmar selección" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import avatarDefault from "@/assets/img/Evolve/usuario.png";
import { getDetallePeleadorPendiente, registrarPeleadorPendiente } from "@/utils/api_evento";

const router = useRouter();

const props = defineProps({
  token: { type: String, required: true },
});

// ── Estado de pantalla ────────────────────────────────────────────────────────
const cargando = ref(true);
const linkInvalido = ref(false);
const completo = ref(false);
const registrado = ref(false);
const enviando = ref(false);
const errorMsg = ref("");

// ── Datos del evento ──────────────────────────────────────────────────────────
const nombreEvento = ref("");
const comprasPendientes = ref([]);
const combates = ref([]);

// ── Formulario ────────────────────────────────────────────────────────────────
// { [compra_id]: peleador_id }
const selecciones = ref({});

const seleccionadosCount = computed(
  () => comprasPendientes.value.filter((c) => selecciones.value[c.id]).length
);

const progresoPct = computed(() =>
  comprasPendientes.value.length
    ? Math.round((seleccionadosCount.value / comprasPendientes.value.length) * 100)
    : 0
);

const todasLasSelecciones = computed(
  () => comprasPendientes.value.length > 0 && seleccionadosCount.value === comprasPendientes.value.length
);

// id de peleador -> { nombre, foto }
const peleadorPorId = computed(() => {
  const mapa = {};
  combates.value.forEach((c) => {
    if (c.peleador_a.id) mapa[c.peleador_a.id] = c.peleador_a;
    if (c.peleador_b.id) mapa[c.peleador_b.id] = c.peleador_b;
  });
  return mapa;
});

const resumenSeleccion = computed(() =>
  comprasPendientes.value
    .map((c) => ({
      tipoBoleto: c.tipo_boleto,
      peleador: peleadorPorId.value[selecciones.value[c.id]],
    }))
    .filter((r) => r.peleador)
);

function elegir(compraId, peleadorId) {
  if (!peleadorId) return;
  selecciones.value = { ...selecciones.value, [compraId]: peleadorId };
  errorMsg.value = "";
}

// ── Al montar ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  try {
    const data = await getDetallePeleadorPendiente(props.token);
    if (data.completo) {
      completo.value = true;
      return;
    }
    nombreEvento.value = data.nombre_evento;
    comprasPendientes.value = data.compras_pendientes;
    combates.value = data.combates;
  } catch (e) {
    linkInvalido.value = true;
  } finally {
    cargando.value = false;
  }
});

// ── Confirmación con auto-cierre ──────────────────────────────────────────────
function mostrarExito() {
  const nombres = resumenSeleccion.value.map((r) => r.peleador.nombre).join(", ");

  return Swal.fire({
    icon: "success",
    title: "¡Tu selección quedó registrada!",
    html: `<p>Vas a alentar a <strong>${nombres}</strong>.</p><p>Nos vemos en el evento 🎉</p>`,
    timer: 3000,
    timerProgressBar: true,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
  });
}

// ── Enviar ────────────────────────────────────────────────────────────────────
async function enviar() {
  errorMsg.value = "";

  if (!todasLasSelecciones.value) {
    errorMsg.value = "Elige un peleador para cada boleto pendiente.";
    return;
  }

  enviando.value = true;
  try {
    const payload = {
      selecciones: comprasPendientes.value.map((c) => ({
        compra_id: c.id,
        peleador_id: selecciones.value[c.id],
      })),
    };
    const resultado = await registrarPeleadorPendiente(props.token, payload);
    if (!resultado.actualizadas?.length) {
      errorMsg.value = resultado.errores?.[0]?.detail || "No fue posible guardar tu selección.";
      return;
    }
    registrado.value = true;
    await mostrarExito();
    router.push("/");
  } catch (e) {
    const data = e.response?.data;
    if (data?.errores?.length) errorMsg.value = data.errores[0].detail;
    else if (data?.detail) errorMsg.value = data.detail;
    else errorMsg.value = "No fue posible guardar tu selección. Intenta de nuevo.";
  } finally {
    enviando.value = false;
  }
}
</script>

<style scoped>
/* ── Fondo fijo al viewport — no se deforma aunque el contenido crezca ── */
.seleccion-page {
  position: relative;
  min-height: 100vh;
}
.seleccion-bg {
  position: fixed;
  inset: 0;
  background: url("@/assets/img/Evolve/img_8.jpg") center center / cover no-repeat;
  z-index: 0;
}
.seleccion-bg::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(10, 10, 15, 0.45), rgba(10, 10, 15, 0.65));
}
.seleccion-content {
  position: relative;
  z-index: 1;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
}

.asignar-boleto-card {
  background: #fff;
  border-radius: 20px;
  padding: 2.5rem 2rem;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
}
.seleccion-card {
  max-width: 580px;
  border-top: 5px solid #007bff;
}

/* Estados */
.estado-center {
  text-align: center;
  padding: 1rem 0;
}
.estado-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

/* Encabezado del formulario */
.form-header {
  text-align: center;
  margin-bottom: 1.5rem;
}
.form-kicker {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #007bff;
  background: #eaf3ff;
  padding: 4px 12px;
  border-radius: 999px;
  margin-bottom: 0.75rem;
}
.form-titulo {
  font-weight: 700;
  margin-bottom: 0.25rem;
}
.form-subtitulo {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0;
}
.form-progreso {
  margin-top: 1rem;
}
.progreso-texto {
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 6px;
}
.progreso-bar {
  height: 6px;
  border-radius: 999px;
  background: #edf1f5;
  overflow: hidden;
}
.progreso-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #00c2ff);
  border-radius: 999px;
  transition: width 0.25s ease;
}

/* Secciones por boleto pendiente */
.compra-seccion {
  margin-bottom: 1.5rem;
  padding: 1rem 1rem 0.25rem;
  border: 1px solid #eee;
  border-radius: 16px;
  transition: border-color 0.15s, background 0.15s;
}
.compra-seccion.is-completa {
  border-color: #bfe0ff;
  background: #f7fbff;
}
.compra-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0.85rem;
}
.compra-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #007bff;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
}
.compra-titulo-text {
  font-weight: 600;
  font-size: 0.9rem;
  color: #333;
}
.compra-check {
  color: #22c55e;
  font-size: 1rem;
  margin-left: auto;
}

/* Combates */
.combates-lista {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 340px;
  overflow-y: auto;
  padding: 4px 4px 1rem;
}
.combate-row {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}
.vs-label {
  font-size: 0.7rem;
  font-weight: 800;
  color: #fff;
  background: #cfd6dd;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.peleador-opcion {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 6px;
  border: 2px solid #e5e5e5;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, transform 0.15s;
  text-align: center;
  background: #fff;
  position: relative;
}
.peleador-opcion:hover:not(.is-disabled) {
  border-color: #aac4f5;
  transform: translateY(-2px);
}
.peleador-opcion.is-selected {
  border-color: #007bff;
  background: #f0f6ff;
}
.peleador-opcion.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.peleador-foto {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #eee;
}
.peleador-nombre {
  font-size: 0.75rem;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
.check-icon {
  position: absolute;
  top: 4px;
  right: 4px;
  color: #007bff;
  font-size: 1rem;
  background: #fff;
  border-radius: 50%;
}

/* Resumen — pantalla de éxito */
/* Responsive */
@media (max-width: 420px) {
  .asignar-boleto-card {
    padding: 2rem 1.25rem;
  }
  .peleador-foto {
    width: 46px;
    height: 46px;
  }
  .peleador-nombre {
    font-size: 0.68rem;
  }
}
</style>
